import React from 'react'
import PropTypes from 'prop-types'
import styled from 'react-emotion'
import themeVars from 'ui_theme/site/globals/site.variables'
import { isSafari } from 'util/browser'
import { curry, propFunc, debounce } from 'util/functions'
import { isValidEmail } from 'util/strings'

import { CannedReplyDropdown } from 'ducks/cannedReplies'
import { Flex, ValidatedInput, SUI } from 'shared/ui'
import EmailComposer from 'components/App/DesktopView/shared/Composer/Email'
import RecipientsEditor from 'components/App/DesktopView/shared/Composer/Header/RecipientsEditor'

import MailboxSelectorDropdown from '../../shared/MailboxSelectorDropdown'
import Editor from './Editor'

const EditorContainer = styled('div')`
  display: flex;
  flex-direction: column;
  width: 100%;
  min-width: 0;
  align-self: stretch;
  position: relative;
  padding-top: ${themeVars.mini};
  padding-left: ${themeVars.large};
  padding-right: ${themeVars.large};
  padding-bottom: ${themeVars.tiny};
  background-color: ${themeVars.white};
  border: 1px solid ${themeVars.borderColor};
  border-radius: ${themeVars.defaultBorderRadius};

  & > * {
    flex-grow: 1;
  }

  && .Composer {
    max-height: none;
  }

  && .Dropzone {
    max-height: none;
  }

  && .editorAndAttachments {
    max-height: 60vh;
  }

  && .mce-content-body {
    height: 100%;
  }

  && [class*='Attachments__'] {
    flex-shrink: 0;
    max-height: 190px; /* enough to show one row of images and regular attachments */
    overflow: auto;
  }

  & .groove-inline-placeholder:before {
    opacity: 0.5;
  }
`

const SuifyiedValidatedInput = ({ className, ...rest }) => (
  <SUI className={className}>
    <ValidatedInput {...rest} />
  </SUI>
)

const Input = styled(SuifyiedValidatedInput)`
  width: 100%;
`

const FormRow = styled(Flex)`
  margin-bottom: ${themeVars.large};
  &:last-child {
    margin-bottom: 0;
  }
`

const LastFormRow = styled(FormRow)`
  flex-grow: 1;
  height: 100%;
  min-height: 0;
`

const FieldLabel = styled('div')`
  color: ${themeVars.ultraLight};
  min-width: 51px;
  margin-right: ${themeVars.big};
  margin-top: 1em;
`
const ShowHideButton = styled('div')`
  line-height: 1.5;
  text-transform: uppercase;
  color: ${themeVars.ultraLight};
  font-size: ${themeVars.small};
  flex-shrink: 0;
  flex-grow: 0;
  cursor: pointer;

  & + & {
    margin-left: 1em;
  }
`

const StyledDropdown = styled(MailboxSelectorDropdown)`
  &&& {
    .text {
      margin-top: -2px;
      margin-bottom: -5px;
      line-height: 1.5;
      white-space: nowrap;
      overflow-x: hidden;
      text-overflow: ellipsis;
      width: 100%;
    }
    .menu {
      .item {
        white-space: nowrap !important;
        word-wrap: normal;
        overflow: hidden;
        text-overflow: ellipsis;
      }
    }
  }
`

// eslint-disable-next-line react/no-multi-comp
export default class Composer extends React.Component {
  static propTypes = {
    draft: PropTypes.object.isRequired, // eslint-disable-line react/forbid-prop-types
    isDraftSendable: PropTypes.bool.isRequired,
    ticketId: PropTypes.string.isRequired,
    updateAndSendDraft: PropTypes.func.isRequired,
    updateDraft: PropTypes.func.isRequired,
  }

  state = {
    shouldNotUpdateRecipient: false,
    isInvalidEmail: false,
    optionalFields: {
      cc: false,
      bcc: false,
    },
  }

  componentWillUnmount() {
    this.resetTimers()
    if (this.invalidEmailTimeoutId) clearTimeout(this.invalidEmailTimeoutId)
  }

  resetTimers = () => {
    this.resetNameTimer()
    this.resetEmailTimer()
  }

  resetNameTimer = () => {
    if (this.nameTimerID) clearTimeout(this.nameTimerID)
  }

  resetEmailTimer = () => {
    if (this.emailTimerID) clearTimeout(this.emailTimerID)
  }

  showField = field => () => {
    this.setState({
      optionalFields: { ...this.state.optionalFields, [field]: true },
    })
  }

  saveNameInputRef = node => (this.nameInput = node)

  maybeUpdateToName = newName => {
    // We don't want to update recipient if input lost focus just for a moment
    // e.g. because user clicked an option in dropdown
    this.nameTimerID = setTimeout(() => {
      // clear out any pending debounced updates
      this.handleNameChange.cancel()
      this.updateToName(newName)
    }, 200)
  }

  maybeUpdateToEmail = newEmail => {
    // We don't want to update recipient if input lost focus just for a moment
    // e.g. because user clicked an option in dropdown
    this.emailTimerID = setTimeout(() => {
      // clear out any pending debounced updates
      this.handleEmailChange.cancel()
      this.updateToEmail(newEmail)
    }, 200)
  }

  updateToName = val => {
    const { ticketId, updateDraft, draft } = this.props
    const { to } = draft
    // old semantics - we assume only one recipient (!)
    const last = (Array.isArray(to) ? to[to.length - 1] : to) || {}

    // Its important that every time we update we wipe the whole recipient.
    // We dont want to update another recipient by accident. We preserve only
    // the name and email, and the API can take care of the rest
    return updateDraft(ticketId, 'to', [{ name: val, email: last.email }])
  }

  updateToEmail = val => {
    const { ticketId, updateDraft, removeDraftRecipients, draft } = this.props
    const { to } = draft
    const last = (Array.isArray(to) ? to[to.length - 1] : to) || {}

    this.setState({ isInvalidEmail: false })
    // Dont bother trying to do an update if the same recipient
    if (last.email === val) return Promise.resolve(val)

    // Its important that every time we update we wipe the whole recipient.
    // We dont want to update another recipient by accident. We preserve only
    // the name and email, and the API can take care of the rest
    removeDraftRecipients(ticketId, { to: last })
    return updateDraft(ticketId, 'to', [{ name: last.name, email: val }])
  }

  handleNameUpdate = event =>
    event && event.target && this.maybeUpdateToName(event.target.value)

  handleEmailUpdate = event =>
    event && event.target && this.maybeUpdateToEmail(event.target.value)

  handleNameChange = debounce(this.handleNameUpdate, 750)

  handleEmailChange = debounce(this.handleEmailUpdate, 750)

  handleNameBlur = this.handleNameUpdate

  handleEmailBlur = evt => {
    this.handleEmailUpdate(evt)

    if (evt && evt.target) {
      const email = evt.target.value
      if (!isValidEmail(email))
        // need to account for blur happening because of selecting
        // recipient from the dropdown
        this.invalidEmailTimeoutId = setTimeout(
          () => this.setState({ isInvalidEmail: true }),
          250
        )
    }
  }

  handleOnChange = func => event =>
    event && event.target && func(event.target.value)

  handleDeleteDraft = () => {
    this.resetTimers()
    this.nameInput.focus()
  }

  render() {
    const {
      draft,
      className,
      isDraftSendable,
      ticketId,
      updateAndSendDraft,
      updateDraft,
    } = this.props
    const { isInvalidEmail } = this.state

    const showCcField =
      this.state.optionalFields.cc || (draft.cc && draft.cc.length > 0)
    const showBccField =
      this.state.optionalFields.bcc || (draft.bcc && draft.bcc.length > 0)

    return (
      <Flex
        flexDirection="column"
        css={`
          height: 100%;
        `}
      >
        <FormRow>
          <FieldLabel>To:</FieldLabel>
          <RecipientsEditor
            css={`
              flex-grow: 0;
            `}
            forwardRef={this.saveNameInputRef}
            key={`recipientseditor-to-name-${ticketId}`}
            onBlur={this.handleNameBlur}
            onChange={this.handleNameChange}
            onFocus={this.resetNameTimer}
            placeholder="Customer’s full name"
            recipients={draft.to}
            saveOnTab={false}
            skipOnAdd
            sui
            ticketId={ticketId}
            toType="name"
            type="to"
          />
          <RecipientsEditor
            css={`
              flex-grow: 0;
            `}
            error={isInvalidEmail && 'Invalid email address'}
            key={`recipientseditor-to-email-${ticketId}`}
            noAutoFocus
            onBlur={this.handleEmailBlur}
            onChange={this.handleEmailChange}
            onFocus={this.resetEmailTimer}
            placeholder="Email address"
            recipients={draft.to}
            saveOnTab={false}
            sui
            ticketId={ticketId}
            toType="email"
            type="to"
          />
          {(!showCcField || !showBccField) && (
            <Flex
              alignItems="center"
              justifyContent="flex-end"
              css={`
                flex-grow: 1;
              `}
            >
              {!showCcField && (
                <ShowHideButton onClick={this.showField('cc')}>
                  CC
                </ShowHideButton>
              )}
              {!showBccField && (
                <ShowHideButton onClick={this.showField('bcc')}>
                  BCC
                </ShowHideButton>
              )}
            </Flex>
          )}
        </FormRow>
        {showCcField && (
          <FormRow>
            <FieldLabel>CC:</FieldLabel>
            <RecipientsEditor
              key={`recipientseditor-cc-${ticketId}`}
              type="cc"
              ticketId={ticketId}
              recipients={draft.cc}
              sui
            />
          </FormRow>
        )}
        {showBccField && (
          <FormRow>
            <FieldLabel>BCC:</FieldLabel>
            <RecipientsEditor
              key={`recipientseditor-bcc-${ticketId}`}
              type="bcc"
              ticketId={ticketId}
              recipients={draft.bcc}
              sui
            />
          </FormRow>
        )}

        <FormRow>
          <FieldLabel>From:</FieldLabel>
          <StyledDropdown
            size="small"
            fluid
            onChange={propFunc(curry(updateDraft)(ticketId)('mailboxId'))}
            value={draft.mailboxId || null}
            selection
          />
        </FormRow>
        <FormRow>
          <FieldLabel>Subject:</FieldLabel>
          <Input
            defaultValue={(draft && draft.title) || ''}
            placeholder="Enter your title here"
            fluid
            onBlur={this.handleOnChange(curry(updateDraft)(ticketId)('title'))}
            renderDefaultValueChanges
          />
        </FormRow>
        <LastFormRow>
          <FieldLabel />
          <EditorContainer>
            <div
              css={`
                && {
                  flex-grow: 0;
                  margin-left: auto;
                  margin-bottom: ${themeVars.nano};
                }
              `}
            >
              <CannedReplyDropdown ticketId={'new'} upward={false} />
            </div>
            <EmailComposer
              actionLabel="Send"
              className={className}
              css={`
                min-height: 0;

                &&& {
                  /* Safari has its own idea on how to lay out flexbox
                   * elements, so we need to adjust for that
                   */
                  ${isSafari() && 'height: 100%'};
                }
              `}
              content={draft.body}
              draft={draft}
              editor={Editor}
              header={null}
              hideCollisionIndicator
              isDraftSendable={isDraftSendable}
              isOpen
              onDeleteDraft={this.handleDeleteDraft}
              showAttachmentIcon
              showToolbarIcon
              ticketId={ticketId}
              updateAndSendDraft={updateAndSendDraft}
              wrapperClass="fullHeight"
            />
          </EditorContainer>
        </LastFormRow>
      </Flex>
    )
  }
}
